/**
 * Sawyer C3 — "Customer asked for": the add-ons the customer requested when
 * booking the groom, shown inside the grooming tier step so what was done is
 * what is charged.
 *
 * Each billable request is a ticked checkbox the desk can untick (not done ⇒
 * not billed). The price on each line is the SERVER's charge for this visit
 * (unit × dogs, `priceServiceLeg`), never computed here. Rows the server says
 * are already on the visit as a service, or no longer on the menu, have no
 * checkbox and say why. Five states (DESIGN-PHILOSOPHY): loading, error with
 * Try again, empty and unsupported (an older server) render nothing at all,
 * default with hover / focus-visible rows.
 */
import type { RequestedAddOn, RequestedAddOnsResult } from '../../lib/requested-add-ons-api.js';
import './RequestedAddOns.css';

export type RequestedAddOnsState = { readonly kind: 'loading' } | RequestedAddOnsResult;

export interface RequestedAddOnsControl {
  readonly state: RequestedAddOnsState;
  /** add-on ids the desk has ticked (billable rows only are ever billed). */
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (addOnId: string) => void;
  readonly onRetry: () => void;
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** "$40.00 (2 dogs) (booked at $20.00 each)" — unit compared with unit (refuter 7). */
export function requestedAddOnPriceText(r: RequestedAddOn, dogCount: number): string {
  const parts = [formatMoney(r.totalCents ?? r.catalogPriceCents * Math.max(dogCount, 1))];
  if (dogCount > 1) parts.push(`(${dogCount} dogs)`);
  if (r.priceChanged) {
    parts.push(`(booked at ${formatMoney(r.requestedPriceCents)}${dogCount > 1 ? ' each' : ''})`);
  }
  return parts.join(' ');
}

/** The ticked, billable add-ons' server totals — display only; the legs are priced by the server. */
export function selectedRequestedAddOnsCents(control: RequestedAddOnsControl | null | undefined): number {
  if (!control || control.state.kind !== 'ok') return 0;
  return control.state.requested
    .filter((r) => r.status === 'billable' && control.selected.has(r.addOnId))
    .reduce((sum, r) => sum + (r.totalCents ?? 0), 0);
}

/** The ids an apply should send: ticked AND billable AND actually seen by the desk. */
export function selectedRequestedAddOnIds(control: RequestedAddOnsControl | null | undefined): string[] {
  if (!control || control.state.kind !== 'ok') return [];
  return control.state.requested
    .filter((r) => r.status === 'billable' && control.selected.has(r.addOnId))
    .map((r) => r.addOnId);
}

export function RequestedAddOns({
  control,
  disabled = false,
}: {
  readonly control: RequestedAddOnsControl;
  readonly disabled?: boolean;
}): JSX.Element | null {
  const { state } = control;
  if (state.kind === 'unsupported') return null;
  if (state.kind === 'ok' && state.requested.length === 0) return null;

  return (
    <section
      className="ferro-requested-addons"
      aria-labelledby="ferro-requested-addons-title"
      data-testid="requested-addons"
    >
      <h3 id="ferro-requested-addons-title" className="ferro-requested-addons__title">
        Customer asked for
      </h3>
      {state.kind === 'loading' ? (
        <p className="ferro-requested-addons__status" aria-live="polite" data-testid="requested-addons-loading">
          Checking the customer&apos;s add-on requests…
        </p>
      ) : state.kind === 'error' ? (
        <div className="ferro-requested-addons__error" role="alert" data-testid="requested-addons-error">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-requested-addons__retry"
            onClick={control.onRetry}
            disabled={disabled}
            data-testid="requested-addons-retry"
          >
            Try again
          </button>
        </div>
      ) : (
        <ul className="ferro-requested-addons__list">
          {state.requested.map((r) => {
            if (r.status === 'billable') {
              const id = `requested-addon-${r.addOnId}`;
              return (
                <li key={r.addOnId}>
                  <label htmlFor={id} className="ferro-requested-addons__row" data-testid={`requested-addon-${r.addOnId}`}>
                    <input
                      id={id}
                      type="checkbox"
                      checked={control.selected.has(r.addOnId)}
                      onChange={() => control.onToggle(r.addOnId)}
                      disabled={disabled}
                      data-testid={`requested-addon-check-${r.addOnId}`}
                    />
                    <span className="ferro-requested-addons__name">{r.label}</span>
                    <span className="ferro-requested-addons__price">
                      {requestedAddOnPriceText(r, state.dogCount)}
                    </span>
                  </label>
                </li>
              );
            }
            return (
              <li
                key={r.addOnId}
                className="ferro-requested-addons__row ferro-requested-addons__row--static"
                data-testid={`requested-addon-${r.addOnId}`}
              >
                <span className="ferro-requested-addons__name">
                  {r.label} ·{' '}
                  {r.status === 'already_added' ? 'already added as a service' : 'no longer on the menu, not billed'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
